// Vendors
import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Components
import { DirectMessages, ProjectMessages } from './components';
import { Loader, DispatchUnreadMsgAmount } from '../components';

const Messages = () => {
  const [currentTab, setCurrentTab] = useState('direct');
  const [directMessages, setDirectMessages] = useState(null);
  const [projectMessages, setProjectMessages] = useState(null);
  const [commentTabIDOpen, setCommentTabIDOpen] = useState(0);
  const [dmTabIDOpen, setDmTabIDOpen] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(
    window.wlPipMessages.unreadMessages
  );
  const [showOnlyUnreadDM, setShowOnlyUnreadDM] = useState(
    window.wlPipMessages.unreadMessages.dm > 0 // don't rely on state above, only want this on initial load
  );
  const [showOnlyUnreadPM, setShowOnlyUnreadPM] = useState(
    window.wlPipMessages.unreadMessages.project > 0 // don't rely on state above, only want this on initial load
  );
  const isFIP = window.wlPipConfig.isFIP;
  const IS_ADMIN =
    window.wlPipAdmin !== undefined &&
    window.wlPipAdmin.hasOwnProperty('isAdmin') &&
    window.wlPipAdmin.isAdmin === '1';
  let _isSubscribed = false;

  const handleMarkAsRead = (pid = 0) => {
    if (!pid) return;

    const formData = new FormData();
    formData.append('action', 'wl_mark_as_read');
    formData.append('pid', pid);
    formData.append('type', currentTab);

    if (isFIP) {
      formData.append('fip', true);
    }

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(
        ({
          data: {
            success,
            data: { messages, unreadMessages }
          }
        }) => {
          if (success) {
            if (currentTab === 'projects') {
              setProjectMessages(messages);
            } else if (currentTab === 'direct') {
              setDirectMessages(messages);
            }

            setUnreadMessages(unreadMessages);
          }

          setLoading(false);
          DispatchUnreadMsgAmount();
        }
      )
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const postNewMessage = (
    newMessage,
    pid = 0,
    recipient = 0,
    taggedProjects = []
  ) => {
    if (!newMessage) return;

    if (currentTab === 'projects' && !pid) return;

    setLoading(true);

    _isSubscribed = true;

    const formData = new FormData();

    if (currentTab === 'projects') {
      const action = isFIP ? 'wl_fip_post_project_message' : 'wl_pip_post_project_message';
      formData.append('action', action);
      formData.append('pid', pid);
      formData.append('message', newMessage);
      formData.append('type', currentTab);
    } else if (currentTab === 'direct') {
      formData.append('action', 'wl_pip_send_direct_message');
      formData.append('subject', newMessage.subject);
      formData.append('body', newMessage.body);
      formData.append('type', currentTab);

      if (IS_ADMIN) {
        if (recipient === 0) return;

        formData.append('recipient', recipient);

        if (taggedProjects.length > 0) {
          formData.append('taggedProjects', taggedProjects);
        }
      }
    }

    if (isFIP) {
      formData.append('fip', true);
      formData.append('aid', pid);
    }

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(
        ({
          data: {
            success,
            data: { messages, unreadMessages }
          }
        }) => {
          if (_isSubscribed) {
            if (success) {
              if (currentTab === 'projects') {
                setProjectMessages(messages);
              } else if (currentTab === 'direct') {
                setDirectMessages(messages);
              }

              setUnreadMessages(unreadMessages);
            }

            DispatchUnreadMsgAmount();
            _isSubscribed = false;
            setLoading(false);
          }
        }
      )
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const postDMReply = (reply, dm_id = 0) => {
    if (!reply || !dm_id) return;

    setLoading(true);
    _isSubscribed = true;

    const formData = new FormData();
    formData.append('action', 'wl_pip_send_direct_message_reply');
    formData.append('dm_id', dm_id);
    formData.append('reply', reply);

    if (isFIP) {
      formData.append('fip', true);
    }

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(
        ({
          data: {
            success,
            data: { messages, unreadMessages }
          }
        }) => {
          if (_isSubscribed) {
            if (success) {
              setDirectMessages(messages);
            }

            setUnreadMessages(unreadMessages);
            DispatchUnreadMsgAmount();
            setLoading(false);
            _isSubscribed = false;
          }
        }
      )
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  // Load direct/project messages async, but only once.
  useEffect(() => {
    if (currentTab === 'direct' && directMessages !== null) return;
    if (currentTab === 'projects' && projectMessages !== null) return;

    _isSubscribed = true;
    setLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_pip_get_all_messages');
    formData.append('type', currentTab);

    if (isFIP) {
      formData.append('fip', true);
    }

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(
        ({
          data: {
            success,
            data: { messages, unreadMessages }
          }
        }) => {
          if (_isSubscribed) {
            if (success) {
              if (currentTab === 'projects') {
                setProjectMessages(messages);
              } else if (currentTab === 'direct') {
                setDirectMessages(messages);
              }

              setUnreadMessages(unreadMessages);
              setLoading(false);
            }
          }
        }
      )
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });

    return () => (_isSubscribed = false);
  }, [currentTab]);

  return (
    <>
      <h1
        className={`u--color-${isFIP ? 'teal' : 'blue-dark'} u--margin-top-0`}
      >
        Messages
      </h1>
      <div className="grid">
        <div className="grid__col-xs-12">
          <div className="tabs">
            {loading && <Loader />}

            <nav className="tabs__nav">
              <ul>
                <li>
                  <button
                    className={
                      currentTab === 'direct' ? 'toggle__switch--on' : ''
                    }
                    onClick={() => setCurrentTab('direct')}
                  >
                    DIRECT MESSAGES
                    {unreadMessages.dm > 0 && (
                      <span className="tabs__unread-count">
                        {unreadMessages.dm}
                      </span>
                    )}
                  </button>
                </li>
                <li>
                  <button
                    className={
                      currentTab === 'projects' ? 'toggle__switch--on' : ''
                    }
                    onClick={() => setCurrentTab('projects')}
                  >
                    {isFIP ? 'APPLICATION' : 'PROJECT'} MESSAGES
                    {unreadMessages.project > 0 && (
                      <span className="tabs__unread-count">
                        {unreadMessages.project}
                      </span>
                    )}
                  </button>
                </li>
              </ul>
            </nav>

            <div className="tabs__content">
              <div
                className={`toggle__target ${
                  currentTab === 'direct' ? 'toggle__target--visible' : ''
                }`}
              >
                {loading === false && (
                  <DirectMessages
                    IS_ADMIN={IS_ADMIN}
                    isFIP={isFIP}
                    messages={directMessages}
                    unreadMessages={unreadMessages}
                    showOnlyUnreadDM={showOnlyUnreadDM}
                    setShowOnlyUnreadDM={setShowOnlyUnreadDM}
                    loading={loading}
                    dmTabIDOpen={dmTabIDOpen}
                    setDmTabIDOpen={setDmTabIDOpen}
                    postNewMessage={postNewMessage}
                    postDMReply={postDMReply}
                    handleMarkAsRead={handleMarkAsRead}
                  />
                )}
              </div>
              <div
                className={`toggle__target ${
                  currentTab === 'projects' ? 'toggle__target--visible' : ''
                }`}
              >
                {loading === true ? (
                  <div
                    style={{ display: 'block', height: '60px' }}
                    className="is-loading"
                  />
                ) : (
                  <ProjectMessages
                    IS_ADMIN={IS_ADMIN}
                    isFIP={isFIP}
                    unreadMessages={unreadMessages}
                    showOnlyUnreadPM={showOnlyUnreadPM}
                    setShowOnlyUnreadPM={setShowOnlyUnreadPM}
                    projectMessages={projectMessages}
                    loading={loading}
                    commentTabIDOpen={commentTabIDOpen}
                    setCommentTabIDOpen={setCommentTabIDOpen}
                    handleMarkAsRead={handleMarkAsRead}
                    postNewMessage={postNewMessage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Messages;
